import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import SuppressedPanel from './SuppressedPanel';
import LoadFailedPanel from './LoadFailedPanel';
import {
  Panel,
  PanelEmpty,
  CHART_BASE,
  CHART_GRID
} from '../../../../component/common/designSystem';
import { gameColor } from './gamesTheme';

/**
 * The parts every per-game page shares: fetching, the withheld state, the
 * 30-day trend and the weekday chart.
 *
 * Kept here rather than copied four times because the trend and weekday charts
 * are the two panels that must look identical across games — they are what a
 * reader flips between tabs to compare, and a colour or axis that shifts
 * between them would make two identical shapes look different.
 */

/** Renders a null as a dash. A withheld cell is not a zero. */
export const orDash = (value, suffix = '') =>
  value == null ? '-' : `${formatBigNumber(value)}${suffix}`;

/** Seconds as a human duration. Averages arrive as decimals. */
export const asDuration = (seconds) => {
  if (seconds == null) return '-';
  const total = Math.round(seconds);
  if (total < 60) return `${total}s`;
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  if (mins < 60) return secs ? `${mins}m ${secs}s` : `${mins}m`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ${mins % 60}m`;
};

/**
 * Fetch-and-suppress for one game. Returns `[state, node]`: when `node` is not
 * null the page must render it and stop, which keeps the suppression check
 * impossible to forget at the top of each page.
 */
export const useGameDetail = (fetcher) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  if (loader) return [null, <Loader key="loader" />];
  // Checked before suppression: a failed request is not a small cohort, and
  // rendering the withheld state for it would claim a privacy protection that
  // never actually ran.
  if (stats?.error) return [null, <LoadFailedPanel key="failed" />];
  if (stats?.suppressed) {
    return [
      null,
      <SuppressedPanel
        key="suppressed"
        minimum={stats.minimum}
        what={stats.label ? `${stats.label}` : 'this game'}
      />
    ];
  }
  return [stats, null];
};

/** Plays per day for one game, in that game's own colour. */
export const TrendPanel = ({ stats, subtitle }) => {
  const trend = stats?.trend || [];
  const hasData = trend.some((day) => day.plays > 0);

  const options = {
    chart: { ...CHART_BASE, type: 'area' },
    colors: [gameColor(stats?.key)],
    stroke: { width: 3, curve: 'smooth' },
    fill: { type: 'gradient', gradient: { opacityFrom: 0.35, opacityTo: 0.05 } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: lastNDaysLabels(trend.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { shared: true }
  };

  return (
    <Panel title="Over Time" subtitle={subtitle || 'Last 30 days'}>
      {hasData ? (
        <Chart
          type="area"
          height={260}
          width="100%"
          series={[
            { name: 'Played', data: trend.map((day) => day.plays || 0) },
            { name: 'People', data: trend.map((day) => day.people || 0) }
          ]}
          options={{ ...options, colors: [gameColor(stats?.key), '#9CA3AF'] }}
        />
      ) : (
        <PanelEmpty>Nothing played in the last 30 days.</PanelEmpty>
      )}
    </Panel>
  );
};

TrendPanel.propTypes = { stats: PropTypes.object, subtitle: PropTypes.string };

/**
 * Which day of the week people play. Weekday only, never hour of day: these
 * collections carry no local date, so an hour taken from the stored timestamp
 * is UTC and wrong for anyone outside it — and "who is playing at 2pm" is not
 * a question this section exists to answer.
 */
export const WeekdayPanel = ({ stats, subtitle }) => {
  const weekday = stats?.weekday || [];
  const hasData = weekday.some((row) => row.count > 0);

  const options = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [gameColor(stats?.key)],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '45%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: weekday.map((row) => row.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } }
  };

  return (
    <Panel title="Which Day People Play" subtitle={subtitle || 'Whole period'}>
      {hasData ? (
        <Chart
          type="bar"
          height={260}
          width="100%"
          series={[{ name: 'Played', data: weekday.map((row) => row.count) }]}
          options={options}
        />
      ) : (
        <PanelEmpty>Nothing played yet.</PanelEmpty>
      )}
    </Panel>
  );
};

WeekdayPanel.propTypes = { stats: PropTypes.object, subtitle: PropTypes.string };

/** The footer every per-game page carries. */
export const GameFootnote = () => (
  <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
    Every figure on this page is about the group. No score, win rate, rank or level appears beside
    anybody&apos;s name, here or on the {'“'}Employees{'”'} tab, and there is no leaderboard in this
    section. A dash means too few plays sat behind that figure to report it without pointing at
    individuals.
  </p>
);

/** A horizontal bar of ordered bands — the shape most of these pages need. */
export const BandBar = ({ rows, colors, height = 260, unit = 'plays' }) => {
  const options = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [({ dataPointIndex }) => colors[dataPointIndex % colors.length]],
    plotOptions: {
      bar: { horizontal: true, borderRadius: 3, barHeight: '55%', distributed: true }
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: CHART_GRID,
    xaxis: {
      categories: rows.map((row) => row.label),
      labels: { formatter: (val) => formatBigNumber(val) }
    },
    tooltip: {
      y: {
        formatter: (val, { dataPointIndex }) =>
          `${formatBigNumber(val)} ${unit} — ${rows[dataPointIndex]?.share ?? 0}%`
      }
    }
  };

  return (
    <Chart
      type="bar"
      height={height}
      width="100%"
      series={[{ name: unit, data: rows.map((row) => row.count) }]}
      options={options}
    />
  );
};

BandBar.propTypes = {
  rows: PropTypes.array,
  colors: PropTypes.array,
  height: PropTypes.number,
  unit: PropTypes.string
};
